/**
 * Auto-resize de textareas con snap a líneas completas (KAN-183).
 *
 * El chat clipeaba el texto a mitad de línea porque el tope de altura era un
 * valor fijo en pixels (120px) que no era múltiplo del line-height: la última
 * línea visible quedaba cortada al medio. Este helper garantiza que la altura
 * asignada siempre sea `líneas * line-height + padding + borde`, así ninguna
 * línea queda cortada. Por encima del tope de líneas activa scroll interno.
 */

export interface TextareaMeasurements {
  /** scrollHeight del textarea medido con height:auto (incluye padding, no borde). */
  scrollHeight: number;
  /** Line-height computado. Puede ser NaN si el browser devuelve "normal". */
  lineHeight: number;
  /** Font-size computado, usado como fallback para calcular el line-height. */
  fontSize?: number;
  /** Padding vertical total (arriba + abajo). */
  paddingY: number;
  /** Borde vertical total (arriba + abajo). */
  borderY: number;
  /** Mínimo de líneas visibles (default 2: cuadro de escritura más grande). */
  minLines?: number;
  /** Máximo de líneas visibles antes de scrollear (default 8). */
  maxLines?: number;
}

export interface TextareaHeightResult {
  /** Altura total en px (múltiplo del line-height + padding + borde). */
  height: number;
  /** 'auto' cuando el contenido excede maxLines (hay que scrollear). */
  overflowY: 'auto' | 'hidden';
}

/** Calcula la altura de un textarea alineada a líneas completas. Puro y testeable. */
export function computeTextareaHeight(m: TextareaMeasurements): TextareaHeightResult {
  const minLines = m.minLines ?? 2;
  const maxLines = Math.max(minLines, m.maxLines ?? 8);
  // "normal" => NaN => estimamos 1.5em (default razonable de la app).
  const lineHeight =
    Number.isFinite(m.lineHeight) && m.lineHeight > 0
      ? m.lineHeight
      : Math.round((m.fontSize || 16) * 1.5);

  // Líneas que ocupa el contenido (scrollHeight incluye padding, lo restamos).
  // Redondeo hacia arriba: media línea que empieza necesita su línea completa.
  const contentLines = Math.ceil((m.scrollHeight - m.paddingY) / lineHeight);
  const lines = Math.max(minLines, Math.min(maxLines, contentLines));

  return {
    height: lines * lineHeight + m.paddingY + m.borderY,
    overflowY: contentLines > maxLines ? 'auto' : 'hidden',
  };
}

/**
 * Mide el textarea con height:auto, calcula la altura alineada a líneas y la
 * aplica junto con el overflow. Pensado para llamarse desde onInput y desde un
 * efecto sobre el valor controlado (así el texto dictado, que entra
 * programáticamente sin evento input del DOM, también dispara el resize).
 */
export function resizeTextareaToContent(
  el: HTMLTextAreaElement,
  opts: { minLines?: number; maxLines?: number } = {},
): TextareaHeightResult {
  const cs = window.getComputedStyle(el);
  const fontSize = parseFloat(cs.fontSize) || 16;
  const rawLineHeight = parseFloat(cs.lineHeight);

  el.style.height = 'auto';
  const result = computeTextareaHeight({
    scrollHeight: el.scrollHeight,
    lineHeight: rawLineHeight,
    fontSize,
    paddingY: parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) || 0,
    borderY: parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth) || 0,
    ...opts,
  });
  el.style.height = `${result.height}px`;
  el.style.overflowY = result.overflowY;
  return result;
}
